import { Controller } from "@hotwired/stimulus";

// Keeps the drill-down row whose member list is showing in the migration's members frame marked
// aria-current (the row's CSS gives it its hover look) until another row's list loads. Clicking
// the selected row again deselects it and clears the list, and that click never reaches Turbo.
// Mounted (pos_migration_members_scope_data) on an element holding every drill-down into the frame
// and the frame itself: the composition on the Locations card, the whole migration body on a
// location's Migration tab, whose All members heading and token-batch links sit outside the
// composition. A drill-down outside the scope would load a list nobody recorded asking for.
//
// Selection follows two signals: the click itself (instant feedback) and the frame's
// turbo:frame-load (what actually loaded - the frame's src - so a stale click never wins).
// Paging inside the frame changes only the page param, which the comparison ignores. What the
// operator last asked for is tracked apart from the frame: deselecting cannot cancel a request
// already in flight, and Turbo writes the response URL back to src before announcing the load,
// so a load that arrives when nothing is wanted is discarded rather than trusted.
//
// A response with no frame in it (a redirect when the pairing went away, a 404, the POS-unavailable
// page) would otherwise leave "Content missing" in the frame: it is followed as a whole-page
// navigation instead, which is what the link did before it loaded inline.
export default class extends Controller {
  static targets = ["row"];

  connect() {
    this.wanted = null; // the key of the list the operator asked for; null when nothing should show
    this.boundOnFrameLoad = this.onFrameLoad.bind(this);
    this.boundOnFrameMissing = this.onFrameMissing.bind(this);
    this.element.addEventListener("turbo:frame-load", this.boundOnFrameLoad);
    this.element.addEventListener("turbo:frame-missing", this.boundOnFrameMissing);
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.boundOnFrameLoad);
    this.element.removeEventListener("turbo:frame-missing", this.boundOnFrameMissing);
  }

  // Any drill-down into the frame (a tile row, a bar segment, the mirror legend) records what is
  // wanted; only a tile row is highlighted for it.
  select(event) {
    const link = event.target.closest("a[data-turbo-frame]");
    if (!link || !this.element.contains(link)) return;
    // a modified or non-primary click opens a tab, or nothing: Turbo ignores it, so must the selection
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (link.hasAttribute("aria-current")) {
      // Turbo skips clicks whose default was prevented, so the frame is left alone here and emptied
      // by hand; dropping src lets the same row load again on the next click.
      event.preventDefault();
      this.wanted = null;
      this.highlight(null);
      this.clearFrame();
    } else {
      this.wanted = this.key(link.href);
      if (this.rowTargets.includes(link)) this.highlight(link.href);
    }
  }

  onFrameLoad(event) {
    const frame = event.target;
    if (!(frame instanceof Element) || !frame.matches("turbo-frame")) return;

    if (this.wanted === null) {
      // a load that outlived its deselect: nothing should be showing
      this.clearFrame();
    } else if (frame.src) {
      this.highlight(frame.src);
    }
  }

  onFrameMissing(event) {
    event.preventDefault();
    this.navigate(event.detail.response.url);
  }

  navigate(url) {
    window.location.assign(url);
  }

  // url: the list now showing, or null for none
  highlight(url) {
    const key = url && this.key(url);
    this.rowTargets.forEach((row) => {
      if (key !== null && this.key(row.href) === key) {
        row.setAttribute("aria-current", "true");
      } else {
        row.removeAttribute("aria-current");
      }
    });
  }

  clearFrame() {
    const frame = this.element.querySelector("turbo-frame");
    if (!frame) return;
    frame.removeAttribute("src");
    frame.innerHTML = "";
  }

  key(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete("page");
    parsed.searchParams.sort();
    return `${parsed.pathname}?${parsed.searchParams}`;
  }
}
